import React, { useEffect, useState } from "react";
import { Mail, Phone, Building2, Globe, MessageSquare } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { API } from "../../data/constants";

const TABS = [
  { key: "contact", label: "Contact form", endpoint: "/admin/leads/contact" },
  { key: "foot-traffic", label: "Foot-traffic audits", endpoint: "/admin/leads/foot-traffic" },
  { key: "dsp", label: "DSP access", endpoint: "/admin/leads/dsp" }
];

const formatTime = (ts) => {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
};

const ContactRow = ({ lead }) => (
  <div className="bg-white rounded-2xl shadow-card p-5" data-testid={`lead-contact-${lead.id}`}>
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <p className="font-display font-bold text-gray-dark">{lead.name}</p>
        <p className="text-sm text-gray-medium flex items-center gap-2 mt-1">
          <Mail size={13} /> {lead.email}
          {lead.phone && (<><span>·</span><Phone size={13} /> {lead.phone}</>)}
        </p>
        {lead.business_name && (
          <p className="text-sm text-gray-medium flex items-center gap-2 mt-1">
            <Building2 size={13} /> {lead.business_name}
          </p>
        )}
      </div>
      <div className="text-right">
        <p className="text-xs text-gray-medium">{formatTime(lead.timestamp)}</p>
        <div className="flex gap-2 mt-1 justify-end">
          {lead.region && <span className="text-xs bg-gray-light rounded-full px-2 py-0.5">{lead.region}</span>}
          {lead.source && <span className="text-xs bg-orange-primary/10 text-orange-primary rounded-full px-2 py-0.5 font-medium">{lead.source}</span>}
        </div>
      </div>
    </div>
    {lead.message && (
      <div className="mt-3 p-3 bg-gray-light rounded-lg text-sm text-gray-dark flex gap-2">
        <MessageSquare size={14} className="text-orange-primary flex-shrink-0 mt-0.5" />
        <span>{lead.message}</span>
      </div>
    )}
  </div>
);

const AuditRow = ({ lead }) => (
  <div className="bg-white rounded-2xl shadow-card p-5" data-testid={`lead-audit-${lead.id}`}>
    <div className="flex items-start justify-between flex-wrap gap-4">
      <div>
        <p className="font-display font-bold text-gray-dark">{lead.name} · {lead.business_name}</p>
        <p className="text-sm text-gray-medium flex items-center gap-2 mt-1">
          <Mail size={13} /> {lead.email} <span>·</span> <Phone size={13} /> {lead.phone}
        </p>
        {lead.website && (
          <p className="text-sm text-gray-medium flex items-center gap-2 mt-1">
            <Globe size={13} /> {lead.website}
          </p>
        )}
      </div>
      <div className="text-right">
        <p className="text-xs text-gray-medium">{formatTime(lead.timestamp)}</p>
        {lead.region && <span className="text-xs bg-gray-light rounded-full px-2 py-0.5">{lead.region}</span>}
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3 text-xs text-gray-medium">
      {lead.business_type && <span><strong>Type:</strong> {lead.business_type}</span>}
      {lead.locations && <span><strong>Locations:</strong> {lead.locations}</span>}
      {lead.primary_goal && <span><strong>Goal:</strong> {lead.primary_goal}</span>}
    </div>
    {lead.notes && (
      <div className="mt-3 p-3 bg-gray-light rounded-lg text-sm text-gray-dark">{lead.notes}</div>
    )}
  </div>
);

const DspRow = ({ lead }) => (
  <div className="bg-white rounded-2xl shadow-card p-5" data-testid={`lead-dsp-${lead.id}`}>
    <div className="flex items-start justify-between flex-wrap gap-4">
      <div>
        <p className="font-display font-bold text-gray-dark">{lead.name} · {lead.company}</p>
        <p className="text-sm text-gray-medium flex items-center gap-2 mt-1">
          <Mail size={13} /> {lead.email} <span>·</span> <Phone size={13} /> {lead.phone}
        </p>
      </div>
      <p className="text-xs text-gray-medium">{formatTime(lead.timestamp)}</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 text-xs text-gray-medium">
      {lead.monthly_spend && <span><strong>Spend:</strong> {lead.monthly_spend}</span>}
      {lead.primary_goal && <span><strong>Goal:</strong> {lead.primary_goal}</span>}
      {lead.markets && <span className="col-span-full"><strong>Markets:</strong> {lead.markets}</span>}
    </div>
    {lead.notes && <div className="mt-3 p-3 bg-gray-light rounded-lg text-sm text-gray-dark">{lead.notes}</div>}
  </div>
);

const RowFor = ({ tab, lead }) => {
  if (tab === "contact") return <ContactRow lead={lead} />;
  if (tab === "foot-traffic") return <AuditRow lead={lead} />;
  return <DspRow lead={lead} />;
};

const AdminLeadsPage = () => {
  const { authedAxios } = useAuth();
  const [tab, setTab] = useState("contact");
  const [leads, setLeads] = useState([]);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    setError(null);
    setLeads([]);
    const endpoint = TABS.find((t) => t.key === tab).endpoint;
    authedAxios({ url: `${API}${endpoint}` })
      .then(({ data }) => setLeads(data))
      .catch(() => setError("Could not load leads."));
  }, [tab, authedAxios]);

  const filtered = filter
    ? leads.filter((l) =>
        JSON.stringify(l).toLowerCase().includes(filter.toLowerCase())
      )
    : leads;

  return (
    <div data-testid="admin-leads-page">
      <h1 className="font-display text-3xl font-bold text-gray-dark mb-2">Leads</h1>
      <p className="text-gray-medium mb-6">All form submissions from the public site.</p>

      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              tab === t.key ? "bg-orange-primary text-white" : "bg-white text-gray-dark hover:bg-gray-light"
            }`}
            data-testid={`leads-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <input
        type="search"
        placeholder="Filter by name, email, or text…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="w-full px-4 py-3 rounded-lg border border-gray-200 mb-4 focus:ring-2 focus:ring-orange-primary outline-none"
        data-testid="leads-filter-input"
      />

      {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      <p className="text-xs text-gray-medium mb-3">{filtered.length} {filtered.length === 1 ? "lead" : "leads"}</p>

      <div className="space-y-3">
        {filtered.length === 0 && !error && (
          <div className="bg-white rounded-2xl p-8 text-center text-gray-medium">No leads yet.</div>
        )}
        {filtered.map((l) => (
          <RowFor key={l.id} tab={tab} lead={l} />
        ))}
      </div>
    </div>
  );
};

export default AdminLeadsPage;
